import { useCallback, useMemo } from 'react';

import type { CsvExportParams, ExcelExportParams, GridReadyEvent } from 'ag-grid-enterprise';

import type { ReportGridProps } from '@feat/reports/components/types/report-grid.props';

import ReportCreatedDateStatusPanel from '@feat/reports/components/report-created-date-status-panel';
import { useMergePurgeStore } from '@feat/reports/context';
import { useDefaultGridOptions } from '@hooks/grid/default-grid-options.hook';
import { useDefaultGridTheme } from '@hooks/grid/default-grid-theme.hook';

import { useDefaultColumnDefinition } from './default-column-definition.hook';
import { useDefaultColumnGroupDefinition } from './default-column-group-definition.hook';
import { useGetColumns } from './get-columns.hook';
import { useGetContextMenuItems } from './get-context-menu-items.hook';
import { useGetReportData } from './report-grid-data.hook';

/**
 * Custom hook to configure the report grid for displaying report data.
 *
 * This hook provides all necessary configuration and data for rendering an AG Grid instance,
 * including column definitions, export parameters, grid options, theme, context menu items,
 * and loading state. It fetches report data based on the provided `view` and `mailingId`,
 * and prepares export parameters for CSV and Excel formats.
 *
 * @template TData - The shape of the data rows in the grid.
 * @param props - The properties required to configure the report grid.
 * @param props.mailingId - The identifier for the mailing to fetch report data for.
 * @param props.view - The report view type to determine columns and data.
 * @returns An object containing grid configuration, data, export parameters, and utility functions:
 * - `columns`: Column definitions for the grid.
 * - `csvExportParams`: Parameters for exporting grid data to CSV.
 * - `data`: The report data to be displayed in the grid.
 * - `defaultColumnDefinition`: Default column definition for the grid.
 * - `defaultColumnGroupDefinition`: Default column group definition for the grid.
 * - `defaultGridOptions`: Default grid options.
 * - `excelExportParams`: Parameters for exporting grid data to Excel.
 * - `getContextMenu`: Function to get context menu items for the grid.
 * - `isLoading`: Boolean indicating if the data is loading or fetching.
 * - `onGridReady`: Callback for when the grid is ready.
 * - `theme`: The grid theme configuration.
 */
export const useReportGridConfiguration = <TData extends Record<string, unknown>>({
  hasStatusBar = true,
  mailingId,
  view,
}: ReportGridProps) => {
  const defaultColumnDefinition = useDefaultColumnDefinition();
  const defaultColumnGroupDefinition = useDefaultColumnGroupDefinition();
  const setGridApi = useMergePurgeStore(({ setGridApi }) => {
    return setGridApi;
  });
  const baseGridOptions = useDefaultGridOptions({ hasStatusBar });
  const defaultGridOptions = useMemo(() => {
    if (!hasStatusBar) {
      return baseGridOptions;
    }

    return {
      ...baseGridOptions,
      statusBar: {
        statusPanels: [
          { align: 'left' as const, statusPanel: ReportCreatedDateStatusPanel },
          ...(baseGridOptions.statusBar?.statusPanels ?? []),
        ],
      },
    };
  }, [baseGridOptions, hasStatusBar]);
  const theme = useDefaultGridTheme();
  const getContextMenu = useGetContextMenuItems();
  const { data: result, isFetching, isLoading } = useGetReportData<TData>(view, { mailingId });
  const columns = useGetColumns(view, result.dynamicColumnCount);

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      setGridApi(view, event.api);
    },
    [setGridApi, view]
  );

  const data = useMemo(() => {
    return result.success ? (result.data ?? []) : [];
  }, [result]);

  const csvExportParams = useMemo<CsvExportParams>(() => {
    // Export the grid's displayed columns in their display order (ag-grid's default
    // when columnKeys is omitted) so the export matches the UI. Deriving columnKeys
    // from the first data row keyed off the backend's serialization order, dropped
    // columns absent from that row, and leaked hidden columns. See HSPDST-300.
    return {
      skipColumnGroupHeaders: true,
    };
  }, []);

  const excelExportParams = useMemo<ExcelExportParams>(() => {
    return {};
  }, []);

  return {
    columns,
    csvExportParams,
    data,
    defaultColumnDefinition,
    defaultColumnGroupDefinition,
    defaultGridOptions,
    excelExportParams,
    getContextMenu,
    isLoading: isFetching || isLoading,
    onGridReady,
    theme,
  };
};
